import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { GALLERY } from '../data/villaData';

const GALLERY_TITLE = {
  en: 'Gallery', sq: 'Galeria', el: 'Συλλογή', de: 'Galerie', fr: 'Galerie',
  da: 'Galleri', zh: '画廊', pl: 'Galeria', cs: 'Galerie', nl: 'Galerij'
};
const GALLERY_EYEBROW = {
  en: 'Refined Elegance', sq: 'Elegancë e Rafinuar', el: 'Εκλεπτυσμένη Κομψότητα',
  de: 'Verfeinerte Eleganz', fr: 'Élégance Raffinée', da: 'Raffineret Elegance', zh: '精致优雅',
  pl: 'Wyrafinowana Elegancja', cs: 'Vytříbená Elegance', nl: 'Verfijnde Elegantie'
};
const GALLERY_SUB = {
  en: 'Thoughtfully Curated Details', sq: 'Detaje t\u00eb p\u00ebrzgjedhura me kujdes',
  el: 'Προσεκτικά Επιλεγμένες Λεπτομέρειες', de: 'Sorgfältig kuratierte Details',
  fr: 'Détails soigneusement choisis',
  da: 'Omhyggeligt udvalgte detaljer', zh: '精心策划的细节',
  pl: 'Starannie dobrane detale', cs: 'Pečlivě vybrané detaily', nl: 'Zorgvuldig samengestelde details'
};

export default function Gallery() {
  const { lang } = useLanguage();
  const [active, setActive] = useState(null);

  const close = () => setActive(null);
  const next = (e) => { e?.stopPropagation(); setActive((i) => (i + 1) % GALLERY.length); };
  const prev = (e) => { e?.stopPropagation(); setActive((i) => (i - 1 + GALLERY.length) % GALLERY.length); };

  return (
    <section className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-sm tracking-[0.3em] uppercase text-[#b8945e] font-medium mb-4">
            {GALLERY_EYEBROW[lang] || GALLERY_EYEBROW.en}
          </p>
          <h2 className="font-serif text-4xl md:text-5xl text-stone-900 mb-3">
            {GALLERY_TITLE[lang] || GALLERY_TITLE.en}
          </h2>
          <p className="text-stone-600">{GALLERY_SUB[lang] || GALLERY_SUB.en}</p>
          <div className="w-20 h-px bg-[#b8945e] mx-auto mt-6" />
        </div>

        {/* Masonry-style grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 auto-rows-[160px] md:auto-rows-[200px]">
          {GALLERY.map((src, i) => {
            // Variable sizing for visual interest
            const layouts = [
              'row-span-2 col-span-2', 'row-span-1', 'row-span-1', 'row-span-2',
              'row-span-1', 'row-span-1', 'row-span-2', 'row-span-1',
              'row-span-1', 'row-span-2 col-span-2', 'row-span-1',
              'row-span-1', 'row-span-1', 'row-span-2'
            ];
            return (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`group relative overflow-hidden rounded-2xl ${layouts[i] || ''} cursor-zoom-in`}
              >
                <img
                  src={src}
                  alt={`Moonlit Villa ${i + 1}`}
                  loading={i < 6 ? 'eager' : 'lazy'}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
              </button>
            );
          })}
        </div>
      </div>

      {/* Lightbox */}
      {active !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={close}
        >
          <button
            onClick={close}
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          >
            <X size={22} />
          </button>
          <button
            onClick={prev}
            className="absolute left-4 md:left-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          >
            <ChevronLeft size={26} />
          </button>
          <button
            onClick={next}
            className="absolute right-4 md:right-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          >
            <ChevronRight size={26} />
          </button>
          <img
            src={GALLERY[active]}
            alt=""
            onClick={(e) => e.stopPropagation()}
            className="max-w-[92vw] max-h-[88vh] object-contain rounded-xl shadow-2xl"
          />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm">
            {active + 1} / {GALLERY.length}
          </div>
        </div>
      )}
    </section>
  );
}
